import { getCurrentOrg } from '.';
import type { CountryFilterMode } from '../components/CountryFilterDropdown';
import {
  ConversationApiService,
  ParticipantApiService,
} from '../services/conversation-participants';
import { OpenAPI as ConversationParticipants } from '../services/conversation-participants';

import type {
  ConversationAudioFile,
  ConversationDto,
  MessageDto,
  ParticipantInfoDto,
  ConversationCostStatistics,
} from '../services/conversation-participants';

export type MessageResponse = MessageDto;
type ConversationResponseDTO = ConversationDto;
export type ConversationResponse = ConversationResponseDTO & {
  user_phone?: string;
  outbound_phone?: string;
  inbound_phone?: string;
};
export type ParticipantInfoResponse = ParticipantInfoDto;
export type ConversationAudioFileResponse = ConversationAudioFile;
export type GetConversationsFilters = {
  startedAt?: string;
  endedAt?: string;
  completedConversations?: boolean;
  conversationId?: string;
  userId?: string;
  userName?: string;
  agentId?: string;
  agentName?: string;
  agentTitle?: string;
  moreThan?: number;
  lessTnan?: number;
  conversationChannel?: ApiConversationChannel;
  mode?: CountryFilterMode;
  countries?: Array<string>;
  metricName?: string;
  metricValue?: string;
  knowledgeBaseId?: string;
};

export type SelfCostStatistics = ConversationCostStatistics;

export type ApiConversationChannel =
  | 'mobile'
  | 'web'
  | 'phone_call'
  | 'whatsapp'
  | 'web-video'
  | 'web-audio'
  | 'mobile-video'
  | 'mobile-audio'
  | 'app';

export type ConversationsPayload = {
  xAiAgentOrganizationContext: string;
  botMasterId?: string;
  startedAt?: string;
  endedAt?: string;
  completedConversations?: boolean;
  conversationId?: string;
  userId?: string;
  userName?: string;
  agentId?: string;
  agentTitle?: string;
  agentName?: string;
  page?: number;
  size: number;
  sort?: Array<string>;
};

export const getConversationCountries = () => {
  return ConversationApiService.getConversationCountries(getCurrentOrg());
};

export const getConversation = (conversationId: string) => {
  return ConversationApiService.getConversation1(
    conversationId,
    getCurrentOrg()
  );
};
export const getConversationParticipantInfo = (externalId: string) => {
  return ParticipantApiService.getParticipantByExternalId1(
    externalId,
    getCurrentOrg()
  );
};
export const getConversationMessages = (
  conversationId: string,
  translationLanguage?: 'EN'
) => {
  return ConversationApiService.getMessagesByConversationId(
    getCurrentOrg(),
    conversationId,
    translationLanguage
  );
};
export const getConversationAudio = (conversationId: string) => {
  return ConversationApiService.getAudioFiles(conversationId, getCurrentOrg());
};
export const getConversationSelfCostStatistics = (conversationId: string) => {
  return ConversationApiService.getSelfCostStatistics(
    conversationId,
    getCurrentOrg()
  );
};

export const getConversations = (
  botMasterId: string,
  page: number,
  size: number,
  filters: GetConversationsFilters,
  sort?: SortState<string>
) => {
  const {
    userId,
    userName,
    endedAt,
    agentId,
    startedAt,
    agentName,
    agentTitle,
    conversationId,
    countries,
    mode,
    completedConversations,
    moreThan,
    lessTnan,
    conversationChannel,
    metricName,
    metricValue,
    knowledgeBaseId,
  } = filters;

  return ConversationApiService.getConversations(
    getCurrentOrg(),
    botMasterId,
    startedAt,
    endedAt,
    completedConversations,
    conversationId,
    userId,
    userName,
    agentId,
    agentTitle,
    agentName,
    mode,
    countries ? countries : [],
    conversationChannel,
    lessTnan,
    moreThan,
    page,
    size,
    sort?.name ? [`${sort.name},${sort.direction}`] : [],
    metricName,
    metricValue,
    knowledgeBaseId
  );
};

const buildExportConversationsUrl = (
  botMasterId: string,
  filters: GetConversationsFilters
): string => {
  const {
    endedAt,
    countries,
    mode,
    completedConversations,
    startedAt,
    conversationId,
    userId,
    userName,
    agentId,
    agentName,
    agentTitle,
    conversationChannel,
    lessTnan,
    moreThan,
    metricName,
    metricValue,
    knowledgeBaseId,
  } = filters;

  const baseUrl =
    process.env.NODE_ENV === 'development'
      ? '/app'
      : process.env.REACT_APP_BILLING_BASE_API_URL || '';

  const queryParams = new URLSearchParams();

  if (botMasterId) queryParams.append('bot_master_id', botMasterId);
  if (startedAt) queryParams.append('started_at', startedAt);
  if (endedAt) queryParams.append('ended_at', endedAt);
  if (completedConversations !== undefined)
    queryParams.append(
      'completed_conversations',
      String(completedConversations)
    );
  if (conversationId) queryParams.append('conversation_id', conversationId);
  if (userId) queryParams.append('user_id', userId);
  if (userName) queryParams.append('user_name', userName);
  if (agentId) queryParams.append('agent_id', agentId);
  if (agentTitle) queryParams.append('agent_title', agentTitle);
  if (agentName) queryParams.append('agent_name', agentName);
  if (mode) queryParams.append('mode', mode);
  if (countries && countries.length > 0) {
    countries.forEach(country => queryParams.append('countries', country));
  }
  if (conversationChannel) queryParams.append('channel', conversationChannel);
  if (lessTnan !== undefined) queryParams.append('less_then', String(lessTnan));
  if (moreThan !== undefined) queryParams.append('more_then', String(moreThan));
  if (metricName) queryParams.append('metric_name', metricName);
  if (metricValue !== undefined) {
    if (Array.isArray(metricValue)) {
      metricValue.forEach(value =>
        queryParams.append('metric_value', String(value))
      );
    } else {
      queryParams.append('metric_value', String(metricValue));
    }
  }
  if (knowledgeBaseId)
    queryParams.append('knowledge_base_id', knowledgeBaseId);

  return `${baseUrl}/messages/conversations/export?${queryParams.toString()}`;
};

export const exportConversations = async (
  botMasterId: string,
  filters: GetConversationsFilters
): Promise<{ blob: Blob; filename: string }> => {
  const url = buildExportConversationsUrl(botMasterId, filters);

  const response = await fetch(url, {
    method: 'GET',
    headers: {
      Accept: '*/*',
      'x-ai-agent-organization-context': getCurrentOrg(),
      ...(ConversationParticipants.TOKEN && {
        Authorization: `Bearer ${ConversationParticipants.TOKEN}`,
      }),
    },
    redirect: 'follow',
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Export failed: ${response.status} - ${errorText}`);
  }

  const blob = await response.blob();
  
  const contentDisposition = response.headers.get('content-disposition');
  let filename = `conversations_${Date.now()}.json`;
  
  if (contentDisposition) {
    const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/i);
    if (filenameMatch && filenameMatch[1]) {
      filename = filenameMatch[1];
    }
  }

  return { blob, filename };
};
